import React, { useEffect, useRef, useState } from 'react';
import { heroStore, useHero } from '../lib/heroStore.js';
import Code from '../components/Code.jsx';

const hasCodecs = () => typeof window !== 'undefined' && 'VideoEncoder' in window && window.isSecureContext;

export default function ExportSection() {
  const hasDoc = useHero((s) => !!s.doc);
  const [job, setJob] = useState(null); // {kind, progress, phase}
  const [out, setOut] = useState(null); // {kind, url, size, info, name}
  const [err, setErr] = useState(null);
  const ctl = useRef(null);

  useEffect(() => () => { if (out) URL.revokeObjectURL(out.url); }, [out]);

  const run = async (kind) => {
    const { Pulse, doc, player } = heroStore.get();
    if (!Pulse || !doc) return;
    setErr(null);
    const from = Math.min(doc.duration - 2, Math.max(0, Math.floor((player?.time || 0) * 10) / 10));
    const portrait = doc.height > doc.width;
    const size = kind === 'gif' ? (portrait ? { height: 480 } : { width: 480 }) : (portrait ? { height: 960 } : { width: 960 });
    ctl.current = new AbortController();
    setJob({ kind, progress: 0, phase: 'starting' });
    const onProgress = (p) => setJob({ kind, progress: p.progress || 0, phase: p.phase, frame: p.frame, frames: p.frames });
    try {
      const t0 = performance.now();
      const blob = kind === 'gif'
        ? await Pulse.export.gif(doc, { ...size, fps: 15, from, to: from + 2, onProgress, signal: ctl.current.signal })
        : await Pulse.export.video(doc, { ...size, fps: 30, from, to: from + 2, onProgress, signal: ctl.current.signal });
      const ms = performance.now() - t0;
      if (out) URL.revokeObjectURL(out.url);
      const ext = kind === 'gif' ? 'gif' : blob.info?.container || 'mp4';
      setOut({ kind, url: URL.createObjectURL(blob), size: blob.size, info: blob.info, ms, name: `pulse-hero.${ext}`, from });
    } catch (e) {
      setErr(e.name === 'AbortError' ? 'Cancelled.' : `Export failed: ${e.message}`);
    } finally {
      setJob(null);
      ctl.current = null;
    }
  };

  const codecs = hasCodecs();
  return (
    <section className="sec" id="export" aria-labelledby="export-h">
      <div className="wrap">
        <header className="sec-head">
          <p className="kicker">Publishing</p>
          <h2 id="export-h">Export the hero to video, in this tab.</h2>
          <p className="sec-lead">
            <code>Pulse.export</code> renders a scene at exact frame times with a private, silent player, and
            encodes it in the browser: MP4 or WebM through WebCodecs with in-house muxers, GIF with its own
            quantiser, PNG frames, sprite sheets, a <code>.pulse</code> file or one self-contained HTML page. The
            exporter is never throttled by the governor. These buttons export two seconds of the hero from where
            it is now, at its current stress{' '}and with the soundtrack if you started it.
          </p>
        </header>
        <div className="export-grid">
          <div className="panel">
            <div className="export-actions">
              <button type="button" className="wx-btn wx-btn--primary wx-btn--pill" onClick={() => run('video')} disabled={!hasDoc || !!job || !codecs}>Export 2 s MP4</button>
              <button type="button" className="wx-btn wx-btn--ghost wx-btn--pill" onClick={() => run('gif')} disabled={!hasDoc || !!job}>Export 2 s GIF</button>
              {job && <button type="button" className="linkish" onClick={() => ctl.current?.abort()}>Cancel</button>}
            </div>
            {!codecs && <p className="dim small">Video export needs WebCodecs on a secure page; this browser does not offer it here. GIF still works.</p>}
            {job && (
              <div className="progress" role="status">
                <span className="progress-bar" style={{ width: `${Math.round(job.progress * 100)}%` }} />
                <span className="progress-text">{job.phase}{job.frames ? ` · frame ${job.frame ?? 0} of ${job.frames}` : ''}</span>
              </div>
            )}
            {err && <p className="err">{err}</p>}
            {out && (
              <div className="export-out">
                {out.kind === 'gif'
                  ? <img src={out.url} alt="The exported GIF of the hero scene" />
                  : <video src={out.url} controls loop playsInline muted={!out.info?.audioCodec} aria-label="The exported video of the hero scene" />}
                <p className="export-meta">
                  <a className="wx-btn wx-btn--sm wx-btn--pill" href={out.url} download={out.name}>Download {out.name}</a>
                  <span>{(out.size / 1024).toFixed(0)} KiB</span>
                  {out.info && <span>{out.info.width}×{out.info.height} · {out.info.frames} frames · {out.info.videoCodec || out.info.container}{out.info.audioCodec ? ` + ${out.info.audioCodec}` : ''}</span>}
                  <span>rendered and encoded in {(out.ms / 1000).toFixed(1)} s{out.info?.speed ? ` (${out.info.speed.toFixed(1)}× real time)` : ''}</span>
                </p>
              </div>
            )}
            {!out && !job && !err && <p className="dim">The result appears here, ready to play and download. Nothing is uploaded.</p>}
          </div>
          <div className="panel">
            <h3>The call</h3>
            <Code label="Exporting with Pulse.export">{`const mp4 = await Pulse.export.video(doc, {
  width: 960, fps: 30, from, to: from + 2,
  onProgress: p => bar.value = p.progress,
  signal: controller.signal,
});                       // Blob, video/mp4: H.264 + AAC or Opus

const gif = await Pulse.export.gif(doc, { width: 480, fps: 15, from, to: from + 2 });
const page = await Pulse.export.html(doc);   // one file that plays offline`}</Code>
            <p className="dim small">The same exports run from Studio&rsquo;s Publish dialog and from <code>pulse export scene.json -o out.mp4</code>.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
